import { useEffect, useState } from "react";
import { KeyRound, Plus, Copy, Trash2, Loader2, Code2, RotateCw, Gamepad2, Gauge, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";

const KeyUsage = ({ k, onEditCap }) => {
  const pct = k.monthly_cap ? Math.min(100, Math.round((k.month_used / k.monthly_cap) * 100)) : 0;
  const tone = pct >= 100 ? "bg-red-500" : pct >= 80 ? "bg-amber-400" : "bg-primary";
  return (
    <div className="mt-2 space-y-1" data-testid={`api-key-usage-${k.id}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1"><Gauge size={12} /> <b className="text-foreground">{k.month_used}</b> credits this month{k.monthly_cap ? <> / <b className="text-foreground">{k.monthly_cap}</b> cap</> : " · no cap"}</span>
        <button type="button" onClick={onEditCap} className="underline hover:text-primary" data-testid={`api-key-edit-cap-${k.id}`}>{k.monthly_cap ? "change cap" : "set a cap"}</button>
        <span>{k.generations} generations · {k.total_used} credits total · {k.uses} API calls</span>
        {k.last_used_at && <span>last used {new Date(k.last_used_at).toLocaleString()}</span>}
      </div>
      {k.monthly_cap > 0 && (
        <div className="h-1.5 w-full max-w-md rounded-full bg-background border border-border overflow-hidden">
          <div className={`h-full ${tone} transition-[width] duration-500`} style={{ width: `${pct}%` }} />
        </div>
      )}
      {k.history?.length > 1 && (
        <div className="flex items-end gap-1 h-6" title="Credits per month (last 6 months)">
          {k.history.map((h) => <div key={h.month} className="w-3 rounded-sm bg-primary/60" style={{ height: `${Math.max(8, Math.round((h.credits / Math.max(...k.history.map((x) => x.credits), 1)) * 100))}%` }} title={`${h.month}: ${h.credits}`} />)}
        </div>
      )}
    </div>
  );
};

export default function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [gameName, setGameName] = useState("");
  const [cap, setCap] = useState("");
  const [creating, setCreating] = useState(false);

  const fetchKeys = async () => {
    try {
      const { data } = await api.get("/api-keys");
      setKeys(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchKeys(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      await api.post("/api-keys", { name: name || gameName || "Default Key", game_name: gameName, monthly_cap: Number(cap) || 0 });
      setName(""); setGameName(""); setCap("");
      await fetchKeys();
      toast.success("API key created.");
    } catch {
      toast.error("Could not create key.");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id) => {
    await api.delete(`/api-keys/${id}`);
    await fetchKeys();
    toast.success("Key revoked.");
  };

  const rotate = async (id) => {
    try {
      await api.post(`/api-keys/${id}/rotate`);
      await fetchKeys();
      toast.success("Key rotated — the new secret is active for your whole team.");
    } catch {
      toast.error("Could not rotate key.");
    }
  };

  const editCap = async (k) => {
    const v = window.prompt(`Monthly credit cap for “${k.game_name || k.name}” (0 = unlimited)`, String(k.monthly_cap || 0));
    if (v === null) return;
    try { await api.patch(`/api-keys/${k.id}`, { monthly_cap: Number(v) || 0 }); await fetchKeys(); toast.success("Cap updated."); }
    catch { toast.error("Could not update cap."); }
  };
  const editGame = async (k) => {
    const v = window.prompt("Game / title this key belongs to", k.game_name || "");
    if (v === null) return;
    try { await api.patch(`/api-keys/${k.id}`, { game_name: v }); await fetchKeys(); toast.success("Game updated."); }
    catch { toast.error("Could not update."); }
  };

  const copy = async (v) => {
    try {
      await navigator.clipboard.writeText(v);
      toast.success("Copied to clipboard.");
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = v;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success("Copied to clipboard.");
      } catch {
        toast.error("Could not copy — select and copy manually.");
      }
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-5xl">
      <header className="mb-8">
        <h1 className="font-display text-4xl font-extrabold flex items-center gap-3">
          <KeyRound className="text-primary" /> API Keys
        </h1>
        <p className="text-muted-foreground mt-1">Integrate MusicFlow StudioAi into your apps, backends and game engines.</p>
      </header>

      <div className="rounded-2xl border border-border bg-card p-6 mb-8">
        <label className="text-sm font-medium">Create a key per game</label>
        <p className="text-xs text-muted-foreground mt-0.5">One key per title keeps usage separate; the monthly cap stops a build from spending more than you planned.</p>
        <div className="grid gap-2 mt-3 sm:grid-cols-[1fr_1fr_160px_auto]">
          <Input value={gameName} onChange={(e) => setGameName(e.target.value)} placeholder="Game / title — e.g. Neon Drift" className="bg-background border-border" data-testid="api-key-game-input" />
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Key label — e.g. Unreal Production" className="bg-background border-border" data-testid="api-key-name-input" />
          <Input value={cap} onChange={(e) => setCap(e.target.value.replace(/\D/g, ""))} inputMode="numeric" placeholder="Monthly cap (credits)" className="bg-background border-border" data-testid="api-key-cap-input" />
          <Button onClick={create} disabled={creating} className="bg-primary text-primary-foreground hover:bg-primary/90 shrink-0" data-testid="create-api-key">
            {creating ? <Loader2 className="animate-spin" size={16} /> : <><Plus size={16} className="mr-1" /> Generate</>}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-muted-foreground font-mono animate-pulse">Loading…</div>
      ) : keys.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground" data-testid="empty-keys">
          No API keys yet.
        </div>
      ) : (
        <div className="space-y-3 mb-10">
          {keys.map((k) => (
            <div key={k.id} className="rounded-xl border border-border bg-card p-4 flex items-center justify-between gap-4" data-testid={`api-key-${k.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-medium flex items-center gap-2 flex-wrap">
                  {k.game_name && <span className="inline-flex items-center gap-1 text-primary" data-testid={`api-key-game-${k.id}`}><Gamepad2 size={14} /> {k.game_name}</span>}
                  <span className={k.game_name ? "text-muted-foreground text-sm" : ""}>{k.name}</span>
                  <button type="button" onClick={() => editGame(k)} className="text-muted-foreground hover:text-primary" title="Set game" data-testid={`api-key-edit-game-${k.id}`}><Pencil size={12} /></button>
                </div>
                <div className="font-mono text-xs text-muted-foreground truncate">{k.key}</div>
                <KeyUsage k={k} onEditCap={() => editCap(k)} />
              </div>
              <div className="flex gap-1.5 shrink-0">
                <Button variant="ghost" size="icon" onClick={() => copy(k.key)} className="text-muted-foreground hover:text-primary" data-testid={`copy-key-${k.id}`}>
                  <Copy size={16} />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => rotate(k.id)} className="text-muted-foreground hover:text-primary" data-testid={`rotate-key-${k.id}`} title="Rotate key">
                  <RotateCw size={16} />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => revoke(k.id)} className="text-muted-foreground hover:text-destructive" data-testid={`revoke-key-${k.id}`}>
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center gap-2 mb-3">
          <Code2 size={18} className="text-primary" />
          <h3 className="font-display text-lg font-bold">Quick start</h3>
        </div>
        <pre className="font-mono text-xs text-muted-foreground bg-background border border-border rounded-xl p-4 overflow-x-auto leading-relaxed">{`curl -X POST https://musicflowstudioai.com/api/generations \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -d '{
    "prompt": "dark cyberpunk boss battle",
    "genre": "Electronic",
    "duration": 60,
    "loop": true
  }'`}</pre>
      </div>
    </div>
  );
}
